// Heroic Inspiration (2024): a creature either has it or not (never more than one). Expend it to reroll a die right after
// rolling it; the new roll must be used. Saved on the character as `heroicInspiration = { has, revision, requestId? }`.
// Humans gain it on each Long Rest (Resourceful); a Champion 10 gains it at the start of each of its turns in combat when
// it has none (Heroic Warrior, champion.mjs). The app spends it on the creature's own d20 tests: missed attack rolls,
// failed saving throws and ability checks, rolls made on the sheet, and Initiative; and one damage, healing or Hit Die
// die that landed in its lower half. No imports, so every runtime can load it on its own.
export const HEROIC_INSPIRATION_FEATURE_ID = 'heroic-inspiration';

export const hasHeroicInspiration = character => character?.heroicInspiration?.has === true;

const speciesName = character => String((typeof character?.race === 'string' ? character.race : character?.race?.name ?? character?.race?.Name) || '').trim().toLowerCase();
/** Resourceful (Human): Heroic Inspiration after every Long Rest. */
export const hasResourceful = character => speciesName(character) === 'human';

/** The saved state after gaining it (null when the creature already has it). */
export const grantedHeroicInspiration = character => (hasHeroicInspiration(character) ? null
  : { has: true, revision: (Number(character?.heroicInspiration?.revision) || 0) + 1 });

/**
 * One die is rerolled (2024). With Advantage or Disadvantage that is the lower d20; the higher one stays and the roll mode
 * applies again: Advantage keeps the higher of it and the new die (only the kept die is needed), Disadvantage the lower of
 * the higher die and the new one (needs both `dice`). Returns null when Disadvantage comes without both dice.
 */
export function heroicRerollResult({ rollMode = 'normal', kept, dice = null, newDie }) {
  if (rollMode === 'advantage') return Math.max(Number(kept), newDie);
  if (rollMode !== 'disadvantage') return newDie;
  const both = Array.isArray(dice) && dice.length === 2 && dice.every(Number.isInteger) ? dice : null;
  return both ? Math.min(Math.max(...both), newDie) : null;
}

export const heroicInspirationDecision = (resolutionId, what) => ({
  id: `${resolutionId}:${HEROIC_INSPIRATION_FEATURE_ID}`, featureId: HEROIC_INSPIRATION_FEATURE_ID, title: 'Heroic Inspiration',
  prompt: `Your ${what} missed. Expend Heroic Inspiration to reroll the d20? You must use the new roll.`,
  choices: [{ id: 'use', label: 'Reroll the d20', action: 'apply' }, { id: 'skip', label: 'Keep the roll' }],
});

/** The dice Heroic Inspiration can reroll on the sheet: a damage, healing or Hit Die die (d4 to d12). */
export const HEROIC_DIE_SIDES = Object.freeze([4, 6, 8, 10, 12]);

/**
 * The dice worth offering a reroll from damage or healing `groups` ([{ sides, values }]): those in their lower half
 * (1-3 on a d6), lowest first, one choice per size and value (rerolling either of two 1s is the same), at most `limit`.
 * Each choice names the die it rerolls: `{ id, group, index, sides, value }`.
 */
export function heroicDieChoices(groups, limit = 6) {
  const seen = new Set(), choices = [];
  (Array.isArray(groups) ? groups : []).forEach(({ sides, values } = {}, group) => {
    if (!HEROIC_DIE_SIDES.includes(sides) || !Array.isArray(values)) return;
    values.forEach((value, index) => {
      const key = `${sides}:${value}`;
      if (!Number.isInteger(value) || value * 2 > sides || seen.has(key)) return;
      seen.add(key); choices.push({ id: `d${sides}-${value}`, group, index, sides, value });
    });
  });
  return choices.sort((a, b) => a.value / a.sides - b.value / b.sides || a.sides - b.sides).slice(0, limit);
}

/** The groups with one die replaced by its reroll. */
export const withRerolledDie = (groups, { group, index }, value) => groups.map((entry, at) => (at !== group ? entry
  : { ...entry, values: entry.values.map((die, position) => (position === index ? value : die)) }));

export const heroicDieRerollDecision = (requestId, what, choices) => ({
  id: `${requestId}:${HEROIC_INSPIRATION_FEATURE_ID}`, featureId: HEROIC_INSPIRATION_FEATURE_ID, title: 'Heroic Inspiration',
  prompt: `Expend Heroic Inspiration to reroll one ${what} die? You must use the new roll.`,
  choices: [...choices.map(choice => ({ id: choice.id, label: `Reroll the d${choice.sides} (${choice.value})`, action: 'apply' })),
    { id: 'skip', label: 'Keep the roll' }],
});
